import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Textarea } from './ui/textarea';
import LogoUpload from './LogoUpload';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Search, Plus, Trash2, Save, Globe, ChevronRight, Building2, User, Phone, Mail, MapPin, Package,
  GitMerge, AlertTriangle, ChevronDown, ChevronUp, ArrowLeft, Tag, Star, Wand2, Loader2, Sparkles, Layers,
} from 'lucide-react';
import TagInput from './TagInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TechDatabaseVendors({ token, role, isSuperAdmin }) {
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [properties, setProperties] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorDetail, setVendorDetail] = useState(null);
  const [vendorProducts, setVendorProducts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [showNewContact, setShowNewContact] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', role: '', notes: '', property_id: '' });

  // Portal users state
  const [portalUsers, setPortalUsers] = useState([]);
  const [showNewPortalUser, setShowNewPortalUser] = useState(false);
  const [portalUserForm, setPortalUserForm] = useState({ name: '', email: '', password: '' });

  // Vendor scorecard state
  const [vendorScore, setVendorScore] = useState(null);
  const [showScoreEdit, setShowScoreEdit] = useState(false);
  const [scoreForm, setScoreForm] = useState({ support_quality: 3, integration_ease: 3, value_for_money: 3, reliability: 3, innovation: 3, notes: '' });

  // Merge state
  const [mergeMode, setMergeMode] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);

  // Bulk logo transparency reprocessing
  const [reprocessingAll, setReprocessingAll] = useState(false);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [mergeSelections, setMergeSelections] = useState({});
  const [mergeTarget, setMergeTarget] = useState({});
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [pendingMerge, setPendingMerge] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isAdmin = role === 'admin';

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (searchQuery) params.q = searchQuery;
      const [vendorRes, propRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/vendors`, { headers, params }),
        axios.get(`${BACKEND_URL}/api/properties`, { headers }),
      ]);
      setVendors(vendorRes.data.vendors);
      setTotal(vendorRes.data.total);
      setProperties(propRes.data);
    } catch (err) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, [headers, searchQuery]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const fetchDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/vendors/duplicates`, { headers });
      setDuplicateGroups(res.data.groups);
      // Pre-select all vendors in each group, mark first as target
      const selections = {};
      const targets = {};
      res.data.groups.forEach(g => {
        selections[g.domain] = g.vendors.map(v => v.id);
        // Pick vendor with most products as default target
        const sorted = [...g.vendors].sort((a, b) => (b.product_count || 0) - (a.product_count || 0));
        targets[g.domain] = sorted[0].id;
      });
      setMergeSelections(selections);
      setMergeTarget(targets);
    } catch (err) {
      toast.error('Failed to find duplicates');
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const enterMergeMode = () => {
    setMergeMode(true);
    setSelectedVendor(null);
    fetchDuplicates();
  };

  const exitMergeMode = () => {
    setMergeMode(false);
    setDuplicateGroups([]);
    setExpandedGroup(null);
    setMergeSelections({});
    setMergeTarget({});
  };

  const toggleVendorSelection = (domain, vendorId) => {
    setMergeSelections(prev => {
      const current = prev[domain] || [];
      const isSelected = current.includes(vendorId);
      let updated;
      if (isSelected) {
        updated = current.filter(id => id !== vendorId);
        // If we removed the target, pick the first remaining
        if (mergeTarget[domain] === vendorId && updated.length > 0) {
          setMergeTarget(prev => ({ ...prev, [domain]: updated[0] }));
        }
      } else {
        updated = [...current, vendorId];
      }
      return { ...prev, [domain]: updated };
    });
  };

  const setTargetVendor = (domain, vendorId) => {
    setMergeTarget(prev => ({ ...prev, [domain]: vendorId }));
    // Ensure it's selected
    setMergeSelections(prev => {
      const current = prev[domain] || [];
      if (!current.includes(vendorId)) {
        return { ...prev, [domain]: [...current, vendorId] };
      }
      return prev;
    });
  };

  const prepareMerge = (domain) => {
    const selected = mergeSelections[domain] || [];
    const target = mergeTarget[domain];
    if (!target || selected.length < 2) {
      toast.error('Select at least 2 vendors and mark one as primary');
      return;
    }
    const sourceIds = selected.filter(id => id !== target);
    const group = duplicateGroups.find(g => g.domain === domain);
    const targetVendor = group?.vendors.find(v => v.id === target);
    const sourceVendors = group?.vendors.filter(v => sourceIds.includes(v.id)) || [];
    setPendingMerge({ domain, target, targetVendor, sourceIds, sourceVendors });
    setShowMergeConfirm(true);
  };

  const executeMerge = async () => {
    if (!pendingMerge) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/vendors/merge`, {
        target_vendor_id: pendingMerge.target,
        source_vendor_ids: pendingMerge.sourceIds,
      }, { headers });
      toast.success(`Merged ${res.data.vendors_deleted} vendor(s) into "${res.data.target_vendor_name}" — ${res.data.products_moved} products, ${res.data.contacts_moved} contacts reassigned`);
      setShowMergeConfirm(false);
      setPendingMerge(null);
      setExpandedGroup(null);
      // Re-fetch everything fresh
      await Promise.all([fetchDuplicates(), fetchVendors()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Merge failed');
    }
  };

  const selectVendor = async (v) => {
    setSelectedVendor(v);
    setEditMode(false);
    setEditForm(v);
    setVendorScore(null);
    try {
      const [detailRes, productsRes, contactsRes, scoresRes, portalRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/vendors/${v.id}`, { headers }),
        axios.get(`${BACKEND_URL}/api/products?vendor_id=${v.id}&limit=100`, { headers }),
        axios.get(`${BACKEND_URL}/api/vendors/${v.id}/contacts`, { headers }),
        axios.get(`${BACKEND_URL}/api/vendor-scores?vendor_id=${v.id}`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/portal-users?entity_id=${v.id}&role=vendor_admin`, { headers }).catch(() => ({ data: { users: [] } })),
      ]);
      setVendorDetail(detailRes.data);
      setVendorProducts(productsRes.data.products);
      setContacts(contactsRes.data.contacts);
      setPortalUsers(portalRes.data.users || []);
      const scores = scoresRes.data.scores || [];
      if (scores.length > 0) setVendorScore(scores[0]);
    } catch (err) {
      toast.error('Failed to load vendor details');
    }
  };

  const saveVendor = async () => {
    try {
      const payload = {
        name: editForm.name,
        website: editForm.website || '',
        headquarters: editForm.headquarters || '',
        description: editForm.description || '',
        notes: editForm.notes || '',
        tags: editForm.tags || [],
      };
      if (showNew) {
        await axios.post(`${BACKEND_URL}/api/admin/vendors`, payload, { headers });
        toast.success('Vendor created');
        setShowNew(false);
        fetchVendors();
      } else {
        await axios.put(`${BACKEND_URL}/api/admin/vendors/${editForm.id}`, payload, { headers });
        toast.success('Vendor updated');
        setEditMode(false);
        selectVendor(editForm);
        fetchVendors();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const deleteVendor = async (id) => {
    if (!window.confirm('Delete this vendor? Products will be unlinked.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/vendors/${id}`, { headers });
      toast.success('Vendor deleted');
      setSelectedVendor(null);
      setVendorDetail(null);
      fetchVendors();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const saveContact = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/vendors/${selectedVendor.id}/contacts`, {
        vendor_id: selectedVendor.id,
        name: contactForm.name,
        email: contactForm.email || '',
        phone: contactForm.phone || '',
        role: contactForm.role || '',
        notes: contactForm.notes || '',
        property_id: contactForm.property_id || null,
      }, { headers });
      toast.success('Contact added');
      setShowNewContact(false);
      setContactForm({ name: '', email: '', phone: '', role: '', notes: '', property_id: '' });
      const res = await axios.get(`${BACKEND_URL}/api/vendors/${selectedVendor.id}/contacts`, { headers });
      setContacts(res.data.contacts);
    } catch (err) {
      toast.error('Failed to add contact');
    }
  };

  const deleteContact = async (contactId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/vendor-contacts/${contactId}`, { headers });
      toast.success('Contact deleted');
      const res = await axios.get(`${BACKEND_URL}/api/vendors/${selectedVendor.id}/contacts`, { headers });
      setContacts(res.data.contacts);
    } catch (err) {
      toast.error('Failed to delete contact');
    }
  };

  // ─── MERGE MODE RENDER ───
  if (mergeMode) {
    return (
      <div className="flex flex-col h-full overflow-hidden" data-testid="vendor-merge-mode">
        {/* Merge toolbar */}
        <div className="px-3 md:px-5 py-3 border-b bg-amber-50 dark:bg-amber-950/30 flex flex-wrap items-center gap-2 md:gap-3 shrink-0">
          <Button size="sm" variant="ghost" className="gap-1.5 text-xs" onClick={exitMergeMode} data-testid="exit-merge-mode">
            <ArrowLeft className="h-3.5 w-3.5" /> Exit Merge Mode
          </Button>
          <div className="flex items-center gap-2">
            <GitMerge className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Vendor Deduplication
            </span>
          </div>
          <div className="flex-1" />
          <Badge variant="secondary" className="font-mono text-xs">
            {duplicateGroups.length} duplicate groups found
          </Badge>
        </div>

        {/* Merge content */}
        <ScrollArea className="flex-1">
          <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4">
            {loadingDuplicates ? (
              <div className="text-center py-16">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Analyzing vendors for duplicates...</p>
              </div>
            ) : duplicateGroups.length === 0 ? (
              <div className="text-center py-16">
                <GitMerge className="h-10 w-10 text-muted-foreground/25 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No duplicate vendor groups detected.</p>
                <p className="text-xs text-muted-foreground mt-1">Duplicates are identified by matching website domains.</p>
              </div>
            ) : (
              duplicateGroups.map(group => {
                const isExpanded = expandedGroup === group.domain;
                const selected = mergeSelections[group.domain] || [];
                const target = mergeTarget[group.domain];
                const sourcesCount = selected.filter(id => id !== target).length;

                return (
                  <div key={group.domain} className="border rounded-xl overflow-hidden bg-card" data-testid={`merge-group-${group.domain}`}>
                    {/* Group header */}
                    <button
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/40 transition-colors text-left"
                      onClick={() => setExpandedGroup(isExpanded ? null : group.domain)}
                      data-testid={`merge-group-toggle-${group.domain}`}
                    >
                      <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold">{group.domain}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {group.count} vendors
                        </span>
                      </div>
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>

                    {/* Expanded group content */}
                    {isExpanded && (
                      <div className="border-t px-4 py-3 space-y-3 bg-muted/10">
                        <p className="text-xs text-muted-foreground">
                          Select the <strong>primary</strong> vendor (kept after merge). Other selected vendors will be merged into it — their products and contacts will be reassigned.
                        </p>
                        <div className="space-y-2">
                          {group.vendors.map(v => {
                            const isSelected = selected.includes(v.id);
                            const isTarget = target === v.id;
                            return (
                              <div
                                key={v.id}
                                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                  isTarget ? 'border-green-500 bg-green-50 dark:bg-green-950/20' :
                                  isSelected ? 'border-amber-300 bg-amber-50/50 dark:bg-amber-950/10' :
                                  'border-transparent bg-card'
                                }`}
                                data-testid={`merge-vendor-${v.id}`}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleVendorSelection(group.domain, v.id)}
                                  data-testid={`merge-checkbox-${v.id}`}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium truncate">{v.name}</span>
                                    {isTarget && (
                                      <Badge className="bg-green-600 text-white text-[10px] shrink-0" data-testid={`merge-primary-badge-${v.id}`}>
                                        Primary
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                                    {v.headquarters && <span className="truncate">{v.headquarters}</span>}
                                    <span>{v.product_count} products</span>
                                    <span>{v.contact_count} contacts</span>
                                  </div>
                                </div>
                                {isSelected && !isTarget && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-xs shrink-0"
                                    onClick={() => setTargetVendor(group.domain, v.id)}
                                    data-testid={`set-primary-${v.id}`}
                                  >
                                    Set as Primary
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex items-center justify-between pt-2">
                          <span className="text-xs text-muted-foreground">
                            {sourcesCount > 0
                              ? `${sourcesCount} vendor(s) will be merged into the primary`
                              : 'Select at least 2 vendors to merge'}
                          </span>
                          <Button
                            size="sm"
                            className="gap-1.5 text-xs"
                            disabled={sourcesCount < 1}
                            onClick={() => prepareMerge(group.domain)}
                            data-testid={`merge-execute-${group.domain}`}
                          >
                            <GitMerge className="h-3.5 w-3.5" /> Merge Group
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>

        {/* Merge Confirmation Dialog */}
        <Dialog open={showMergeConfirm} onOpenChange={setShowMergeConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" /> Confirm Vendor Merge
              </DialogTitle>
              <DialogDescription>This action cannot be undone.</DialogDescription>
            </DialogHeader>
            {pendingMerge && (
              <div className="space-y-3 py-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Keep:</span>
                  <span className="ml-2 font-semibold text-green-700 dark:text-green-400">
                    {pendingMerge.targetVendor?.name}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Merge & delete:</span>
                  <div className="ml-2 mt-1 space-y-1">
                    {pendingMerge.sourceVendors.map(v => (
                      <div key={v.id} className="flex items-center gap-2">
                        <span className="text-destructive font-medium">{v.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({v.product_count} products, {v.contact_count} contacts)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <Separator />
                <p className="text-xs text-muted-foreground">
                  Products and contacts from deleted vendors will be reassigned to the primary vendor.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowMergeConfirm(false)}>Cancel</Button>
              <Button onClick={executeMerge} className="gap-1.5 bg-amber-600 hover:bg-amber-700" data-testid="confirm-merge-button">
                <GitMerge className="h-3.5 w-3.5" /> Confirm Merge
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ─── NORMAL MODE RENDER ───
  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="tech-database-vendors">
      {/* Toolbar */}
      <div className="px-3 md:px-5 py-3 border-b bg-card/50 flex flex-wrap items-center gap-2 md:gap-3 shrink-0">
        <div className="relative flex-1 min-w-[150px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            data-testid="vendors-search"
            className="pl-9 h-9"
            placeholder="Search vendors..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex-1" />
        <Badge variant="secondary" className="font-mono text-xs">
          {vendors.length} of {total} vendors
        </Badge>
        {isAdmin && (
          <>
            <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={enterMergeMode} data-testid="enter-merge-mode">
              <GitMerge className="h-3.5 w-3.5" /> Merge Duplicates
            </Button>
            {isSuperAdmin && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs"
              disabled={reprocessingAll}
              onClick={async () => {
                if (!window.confirm('Reprocess all vendor logos to auto-strip white backgrounds? Only touches logos not already processed.')) return;
                setReprocessingAll(true);
                try {
                  const r = await axios.post(`${BACKEND_URL}/api/admin/logos/reprocess-all?entity_type=vendor&limit=500`, {}, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  const { processed, skipped, errors, total_scanned } = r.data || {};
                  toast.success(`Reprocessed ${processed} of ${total_scanned} vendor logos (skipped ${skipped}, errors ${errors})`);
                  fetchVendors();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Bulk reprocess failed');
                } finally {
                  setReprocessingAll(false);
                }
              }}
              data-testid="reprocess-all-logos"
            >
              {reprocessingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
              Reprocess Logos
            </Button>
            )}
            <Button size="sm" className="gap-1.5 text-xs" onClick={() => {
              setShowNew(true);
              setEditMode(true);
              setEditForm({ name: '', website: '', headquarters: '', description: '', notes: '' });
            }}>
              <Plus className="h-3.5 w-3.5" /> New Vendor
            </Button>
          </>
        )}
      </div>

      {/* Content - stacked on mobile */}
      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
        {/* Left: Vendor list */}
        <ScrollArea className={`${selectedVendor && typeof window !== 'undefined' && window.innerWidth < 768 ? 'hidden' : ''} w-full md:w-[350px] md:border-r shrink-0 ${!selectedVendor ? 'flex-1 md:flex-none' : ''}`}>
          <div className="p-3 space-y-0.5">
            {loading ? (
              <p className="text-sm text-muted-foreground p-4 text-center">Loading...</p>
            ) : vendors.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">No vendors match your search</p>
            ) : (
              [...vendors].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(v => (
                <button
                  key={v.id}
                  onClick={() => selectVendor(v)}
                  className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    selectedVendor?.id === v.id ? 'bg-accent ring-1 ring-ring/20' : 'hover:bg-muted/60'
                  }`}
                >
                  <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-1">
                      <span className="truncate">{v.name}</span>
                      {v.logo_path && v.logo_transparency_processed && (
                        <span
                          className="inline-flex items-center shrink-0 text-emerald-500"
                          title="Logo transparency processed"
                          data-testid={`logo-processed-${v.id}`}
                        >
                          <Sparkles className="h-3 w-3" />
                        </span>
                      )}
                    </div>
                    {v.headquarters && (
                      <div className="text-xs text-muted-foreground truncate">{v.headquarters}</div>
                    )}
                  </div>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                </button>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Right: Vendor detail */}
        <div className="flex-1 overflow-auto">
          {showNew ? (
            <ScrollArea className="h-full">
              <div className="p-6 max-w-3xl space-y-5">
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>New Vendor</h2>
                <Separator />
                {renderForm()}
                <div className="flex gap-2 pt-2">
                  <Button onClick={saveVendor} disabled={!editForm.name} className="gap-1.5">
                    <Save className="h-3.5 w-3.5" /> Create Vendor
                  </Button>
                  <Button variant="ghost" onClick={() => { setShowNew(false); setEditForm({}); }}>Cancel</Button>
                </div>
              </div>
            </ScrollArea>
          ) : !selectedVendor ? (
            <div className="h-full flex items-center justify-center hidden md:flex">
              <div className="text-center">
                <Building2 className="h-10 w-10 text-muted-foreground/25 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Select a vendor to view details</p>
              </div>
            </div>
          ) : (
            <ScrollArea className="h-full">
              <div className="p-4 md:p-6 max-w-3xl space-y-5 animate-fade-in">
                {/* Mobile back button */}
                <button
                  className="md:hidden text-xs text-muted-foreground flex items-center gap-1 mb-2"
                  onClick={() => { setSelectedVendor(null); setVendorDetail(null); }}
                >
                  &larr; Back to list
                </button>
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <LogoUpload entityType="vendor" entityId={selectedVendor.id} logoPath={selectedVendor.logo_path} token={token} canReprocess={isSuperAdmin} onUpdate={() => selectVendor(selectedVendor)} />
                    <div>
                      <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {selectedVendor.name}
                      </h2>
                      <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                        {selectedVendor.headquarters && (
                          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {selectedVendor.headquarters}</span>
                        )}
                        {vendorDetail?.product_count != null && (
                          <span className="flex items-center gap-1"><Package className="h-3 w-3" /> {vendorDetail.product_count} products</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {isAdmin && !editMode && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => { setEditMode(true); setEditForm(selectedVendor); }}>Edit</Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteVendor(selectedVendor.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    {editMode && !showNew && (
                      <>
                        <Button size="sm" onClick={saveVendor} className="gap-1"><Save className="h-3.5 w-3.5" /> Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditMode(false); setEditForm(selectedVendor); }}>Cancel</Button>
                      </>
                    )}
                  </div>
                </div>

                <Separator />

                {editMode ? renderForm() : (
                  <>
                    {selectedVendor.website && (
                      <div>
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Links</h3>
                        <a href={selectedVendor.website} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1 truncate">
                          <Globe className="h-3.5 w-3.5 shrink-0" /> {selectedVendor.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                        </a>
                      </div>
                    )}
                    {selectedVendor.description && (
                      <div>
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Description</h3>
                        <p className="text-sm">{selectedVendor.description}</p>
                      </div>
                    )}
                    {(selectedVendor.tags || vendorDetail?.tags || []).length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Tags</h3>
                        <div className="flex flex-wrap gap-1.5">
                          {(selectedVendor.tags || vendorDetail?.tags || []).map((t, i) => (
                            <Badge key={i} variant="secondary" className="text-xs"><Tag className="h-3 w-3 mr-1" />{t}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedVendor.notes && (
                      <div>
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Notes</h3>
                        <p className="text-sm">{selectedVendor.notes}</p>
                      </div>
                    )}
                  </>
                )}

                {!editMode && (
                  <>
                    {/* Multi-platform matrix — which platform types this vendor's products cover */}
                    {vendorProducts.length > 0 && (() => {
                      const matrix = {};
                      vendorProducts.forEach(p => {
                        const plats = (p.platforms || []).length ? p.platforms : (p.platform_name ? [{ id: p.platform_id, name: p.platform_name, category_name: p.category_name, category_color: '#8b5cf6' }] : []);
                        plats.forEach(pl => {
                          const key = pl.category_name || 'Uncategorized';
                          if (!matrix[key]) matrix[key] = { color: pl.category_color || '#8b5cf6', platforms: {} };
                          if (!matrix[key].platforms[pl.id]) matrix[key].platforms[pl.id] = { name: pl.name, products: [] };
                          matrix[key].platforms[pl.id].products.push(p.name);
                        });
                      });
                      const cats = Object.entries(matrix).sort((a, b) => a[0].localeCompare(b[0]));
                      const totalPlats = cats.reduce((s, [, c]) => s + Object.keys(c.platforms).length, 0);
                      const totalCats = cats.length;
                      const multiPlatformProds = vendorProducts.filter(p => (p.platforms || p.platform_ids || []).length > 1).length;
                      return (
                        <div className="mb-6" data-testid="vendor-multi-platform-matrix">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                              <Layers className="h-3.5 w-3.5 text-violet-500" />
                              Platform Coverage
                            </h3>
                            <div className="flex items-center gap-3 text-[11px] text-muted-foreground tabular-nums">
                              <span><strong className="text-violet-700">{totalCats}</strong> categor{totalCats === 1 ? 'y' : 'ies'}</span>
                              <span><strong className="text-violet-700">{totalPlats}</strong> platform{totalPlats === 1 ? '' : 's'}</span>
                              {multiPlatformProds > 0 && <span><strong className="text-violet-700">{multiPlatformProds}</strong> multi-platform</span>}
                            </div>
                          </div>
                          <div className="rounded-lg border overflow-hidden">
                            {cats.map(([catName, catData], ci) => (
                              <div key={catName} className={ci > 0 ? 'border-t' : ''}>
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/40 text-[10px] uppercase tracking-wider font-bold">
                                  <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: catData.color }} />
                                  {catName}
                                  <span className="text-muted-foreground font-normal">· {Object.keys(catData.platforms).length} platform{Object.keys(catData.platforms).length === 1 ? '' : 's'}</span>
                                </div>
                                <div className="p-2 flex flex-wrap gap-1.5">
                                  {Object.entries(catData.platforms).map(([pid, pl]) => (
                                    <span
                                      key={pid}
                                      className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-1 text-[11px]"
                                      title={pl.products.join(', ')}
                                      data-testid={`vendor-matrix-cell-${pid}`}
                                    >
                                      <span className="font-medium">{pl.name}</span>
                                      <span className="text-muted-foreground">· {pl.products.length}</span>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Products */}
                    <div>
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                        Products ({vendorProducts.length})
                      </h3>
                      {vendorProducts.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No products linked.</p>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {vendorProducts.map(p => (
                            <div key={p.id} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/40 text-xs">
                              <Package className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="font-medium truncate">{p.name}</div>
                                <div className="text-muted-foreground truncate">{p.platform_name}</div>
                              </div>
                              <Badge variant="outline" className="text-[10px] shrink-0">{p.status}</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Contacts */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          Contacts ({contacts.length})
                        </h3>
                        {isAdmin && (
                          <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => setShowNewContact(true)}>
                            <Plus className="h-3 w-3" /> Add Contact
                          </Button>
                        )}
                      </div>
                      {contacts.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No contacts added yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {contacts.map(c => (
                            <div key={c.id} className="p-3 rounded-lg bg-muted/40 text-xs group">
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="font-semibold text-sm">{c.name}</div>
                                  {c.role && <div className="text-muted-foreground">{c.role}</div>}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Badge variant="outline" className="text-[10px]">
                                    {c.property_name || 'Global'}
                                  </Badge>
                                  {isAdmin && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                                      onClick={() => deleteContact(c.id)}
                                    >
                                      <Trash2 className="h-3 w-3 text-destructive" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-4 mt-1.5 text-muted-foreground">
                                {c.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</span>}
                                {c.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</span>}
                              </div>
                              {c.notes && <p className="mt-1 text-muted-foreground">{c.notes}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Portal Users */}
                    <Separator />
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          Portal Users ({portalUsers.length})
                        </h3>
                        {isAdmin && (
                          <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => {
                            setPortalUserForm({ name: '', email: '', password: '' });
                            setShowNewPortalUser(true);
                          }} data-testid="add-portal-user">
                            <Plus className="h-3 w-3" /> Add Portal User
                          </Button>
                        )}
                      </div>
                      {portalUsers.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No portal users. Add one to give this vendor access to update their products.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {portalUsers.map(u => (
                            <div key={u.id} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/40 text-xs group">
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm">{u.name}</div>
                                <div className="text-muted-foreground">{u.email}</div>
                              </div>
                              <Badge variant={u.active !== false ? "outline" : "secondary"} className={`text-[9px] ${u.active !== false ? 'text-emerald-600' : 'text-red-600'}`}>
                                {u.active !== false ? 'Active' : 'Disabled'}
                              </Badge>
                              {isAdmin && (
                                <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100" onClick={async () => {
                                  if (!window.confirm(`Delete portal user ${u.name}?`)) return;
                                  try {
                                    await axios.delete(`${BACKEND_URL}/api/admin/portal-users/${u.id}`, { headers });
                                    toast.success('Portal user removed');
                                    setPortalUsers(prev => prev.filter(p => p.id !== u.id));
                                  } catch (e) { toast.error('Failed'); }
                                }}>
                                  <Trash2 className="h-3 w-3 text-destructive" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {/* New Portal User Form */}
                      {showNewPortalUser && (
                        <div className="mt-3 p-3 border rounded-lg space-y-2 bg-card">
                          <Input value={portalUserForm.name} onChange={e => setPortalUserForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" className="h-8 text-xs" />
                          <Input type="email" value={portalUserForm.email} onChange={e => setPortalUserForm(p => ({ ...p, email: e.target.value }))} placeholder="email@vendor.com" className="h-8 text-xs" />
                          <Input type="password" value={portalUserForm.password} onChange={e => setPortalUserForm(p => ({ ...p, password: e.target.value }))} placeholder="Password (6+ chars)" className="h-8 text-xs" />
                          <div className="flex gap-2">
                            <Button size="sm" variant="ghost" className="text-xs" onClick={() => setShowNewPortalUser(false)}>Cancel</Button>
                            <Button size="sm" className="text-xs" disabled={!portalUserForm.name || !portalUserForm.email || portalUserForm.password.length < 6} onClick={async () => {
                              try {
                                await axios.post(`${BACKEND_URL}/api/admin/portal-users`, {
                                  name: portalUserForm.name, email: portalUserForm.email, password: portalUserForm.password,
                                  role: 'vendor_admin', entity_id: selectedVendor.id,
                                }, { headers });
                                toast.success('Portal user created');
                                setShowNewPortalUser(false);
                                const res = await axios.get(`${BACKEND_URL}/api/admin/portal-users?entity_id=${selectedVendor.id}&role=vendor_admin`, { headers });
                                setPortalUsers(res.data.users || []);
                              } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                            }}>Create User</Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Vendor Scorecard */}
                    <Separator />
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                          <Star className="h-3 w-3" /> Vendor Score
                        </h3>
                        {isAdmin && (
                          <Button size="sm" variant="outline" className="text-xs gap-1 h-7" onClick={() => {
                            setScoreForm(vendorScore ? {
                              support_quality: vendorScore.support_quality || 3,
                              integration_ease: vendorScore.integration_ease || 3,
                              value_for_money: vendorScore.value_for_money || 3,
                              reliability: vendorScore.reliability || 3,
                              innovation: vendorScore.innovation || 3,
                              notes: vendorScore.notes || '',
                            } : { support_quality: 3, integration_ease: 3, value_for_money: 3, reliability: 3, innovation: 3, notes: '' });
                            setShowScoreEdit(true);
                          }} data-testid="edit-vendor-score">
                            <Star className="h-3 w-3" /> {vendorScore ? 'Edit' : 'Rate'}
                          </Button>
                        )}
                      </div>
                      {vendorScore ? (
                        <div className="grid grid-cols-5 gap-2 text-center">
                          {[
                            { key: 'support_quality', label: 'Support' },
                            { key: 'integration_ease', label: 'Integration' },
                            { key: 'value_for_money', label: 'Value' },
                            { key: 'reliability', label: 'Reliability' },
                            { key: 'innovation', label: 'Innovation' },
                          ].map(d => (
                            <div key={d.key}>
                              <div className="text-lg font-bold" style={{ color: vendorScore[d.key] >= 4 ? '#10B981' : vendorScore[d.key] >= 3 ? '#F59E0B' : '#EF4444' }}>
                                {vendorScore[d.key] || '-'}
                              </div>
                              <div className="text-[9px] text-muted-foreground">{d.label}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">Not rated yet.</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>

      {/* New Contact Dialog */}
      <Dialog open={showNewContact} onOpenChange={setShowNewContact}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Contact</DialogTitle>
            <DialogDescription>Add a contact for {selectedVendor?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={contactForm.name} onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))} placeholder="Contact name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={contactForm.email} onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))} placeholder="email@company.com" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={contactForm.phone} onChange={e => setContactForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 555-1234" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Role / Title</Label>
              <Input value={contactForm.role} onChange={e => setContactForm(p => ({ ...p, role: e.target.value }))} placeholder="Regional Sales Manager" />
            </div>
            <div>
              <Label className="text-xs">Property (optional — leave empty for global contact)</Label>
              <Select value={contactForm.property_id || 'global'} onValueChange={v => setContactForm(p => ({ ...p, property_id: v === 'global' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Global (all properties)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Global (all properties)</SelectItem>
                  {properties.map(prop => (
                    <SelectItem key={prop.id} value={prop.id}>{prop.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={contactForm.notes} onChange={e => setContactForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Additional notes..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewContact(false)}>Cancel</Button>
            <Button onClick={saveContact} disabled={!contactForm.name}>Add Contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Score Dialog */}
      <Dialog open={showScoreEdit} onOpenChange={setShowScoreEdit}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Star className="h-4 w-4" /> Rate {selectedVendor?.name}</DialogTitle>
            <DialogDescription>Score each dimension from 1 (poor) to 5 (excellent).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {[
              { key: 'support_quality', label: 'Support Quality' },
              { key: 'integration_ease', label: 'Integration Ease' },
              { key: 'value_for_money', label: 'Value for Money' },
              { key: 'reliability', label: 'Reliability' },
              { key: 'innovation', label: 'Innovation' },
            ].map(d => (
              <div key={d.key} className="flex items-center justify-between">
                <Label className="text-xs">{d.label}</Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(v => (
                    <button
                      key={v}
                      onClick={() => setScoreForm(p => ({ ...p, [d.key]: v }))}
                      className={`w-7 h-7 rounded-md text-xs font-bold transition-colors ${
                        scoreForm[d.key] >= v
                          ? v >= 4 ? 'bg-emerald-500 text-white' : v >= 3 ? 'bg-amber-400 text-white' : 'bg-red-400 text-white'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >{v}</button>
                  ))}
                </div>
              </div>
            ))}
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={scoreForm.notes} onChange={e => setScoreForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="General impressions..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowScoreEdit(false)}>Cancel</Button>
            <Button data-testid="save-vendor-score" onClick={async () => {
              try {
                await axios.post(`${BACKEND_URL}/api/admin/vendor-scores`, {
                  vendor_id: selectedVendor.id, ...scoreForm,
                }, { headers });
                toast.success('Score saved');
                setShowScoreEdit(false);
                // Refresh
                const res = await axios.get(`${BACKEND_URL}/api/vendor-scores?vendor_id=${selectedVendor.id}`, { headers });
                const scores = res.data.scores || [];
                if (scores.length > 0) setVendorScore(scores[0]);
              } catch { toast.error('Failed to save score'); }
            }}>
              Save Score
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  function renderForm() {
    return (
      <div className="space-y-4">
        <div>
          <Label className="text-xs">Vendor Name</Label>
          <Input value={editForm.name || ''} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} placeholder="Company name" />
        </div>
        <div>
          <Label className="text-xs">Website</Label>
          <Input value={editForm.website || ''} onChange={e => setEditForm(p => ({ ...p, website: e.target.value }))} placeholder="https://..." />
        </div>
        <div>
          <Label className="text-xs">Headquarters</Label>
          <Input value={editForm.headquarters || ''} onChange={e => setEditForm(p => ({ ...p, headquarters: e.target.value }))} placeholder="City, State" />
        </div>
        <div>
          <Label className="text-xs">Description</Label>
          <Textarea value={editForm.description || ''} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} rows={3} placeholder="About this vendor..." />
        </div>
        <div>
          <Label className="text-xs">Notes</Label>
          <Textarea value={editForm.notes || ''} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Internal notes..." />
        </div>
        <div>
          <Label className="text-xs">Tags</Label>
          <TagInput
            value={editForm.tags || []}
            onChange={tags => setEditForm(p => ({ ...p, tags }))}
            token={token}
          />
        </div>
      </div>
    );
  }
}
